import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./OrderDocsModalAdmin.css";
import { getPedidoDocs, uploadPedidoDocs, deletePedidoDoc } from "../../../services/usuarios_tienda/pedidos_docs";
import { fileURL } from "../../../utils/fileURL";

const MAX_DOCS = 3;

const isPdf = (file) =>
  file?.type === "application/pdf" ||
  String(file?.name || "").toLowerCase().endsWith(".pdf");

const formatMB = (bytes) => {
  const n = Number(bytes || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
};

const normalizePath = (p) => {
  if (!p) return null;
  let s = String(p).trim().replace(/\\/g, "/").replace(/^\/+/, "");
  const idx = s.toLowerCase().indexOf("uploads/");
  if (idx >= 0) s = s.slice(idx);
  return s;
};

function OrderDocsModal({ pedidoId, onClose }) {
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);

  const existingCount = docs?.length || 0;

  const remaining = useMemo(() => Math.max(0, MAX_DOCS - existingCount), [existingCount]);

  const isBusy = busy || loading;
  const canPick = remaining > 0 && !isBusy;
  const canUpload = selected.length > 0 && !isBusy;

  const clearPicker = useCallback(() => {
    setSelected([]);
    setError("");
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const normalizeAndSetFiles = useCallback(
    (incomingFiles) => {
      const files = Array.from(incomingFiles || []);
      if (!files.length) return;

      const pdfs = files.filter(isPdf);
      const allowedCount = Math.max(0, MAX_DOCS - existingCount);

      if (pdfs.length !== files.length) setError("Only PDF files are allowed.");
      else setError("");

      const trimmed = pdfs.slice(0, allowedCount);

      if (pdfs.length > allowedCount) {
        setError(`Max ${MAX_DOCS} PDFs per order. You can add ${allowedCount} more.`);
      }

      setSelected(trimmed);

      if (!trimmed.length && inputRef.current) inputRef.current.value = "";
    },
    [existingCount]
  );

  const onPickFiles = (e) => normalizeAndSetFiles(e.target.files);

  const removeSelected = (name) => {
    setSelected((prev) => {
      const next = prev.filter((f) => f.name !== name);
      if (next.length === 0 && inputRef.current) inputRef.current.value = "";
      return next;
    });
  };

const fetchDocs = useCallback(async () => {
  if (!pedidoId) return;
  try {
    setLoading(true);
    setError("");

    const resp = await getPedidoDocs(pedidoId);

    const list =
      Array.isArray(resp) ? resp :
      Array.isArray(resp?.data) ? resp.data :
      Array.isArray(resp?.docs) ? resp.docs :
      [];

    setDocs(list);
  } catch (e) {
    console.error(e);
    setError(e?.response?.data?.message || e?.message || "Could not load documents.");
  } finally {
    setLoading(false);
  }
}, [pedidoId]);


  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const upload = async () => {
    if (!selected.length) return;
    try {
      setBusy(true);
      setError("");
      await uploadPedidoDocs(pedidoId, selected);
      clearPicker();
      await fetchDocs();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Upload failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const deleteDoc = async (doc) => {
    try {
      setBusy(true);
      setError("");
      await deletePedidoDoc(pedidoId, doc?.id ?? doc?.doc_id);
      await fetchDocs();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Could not delete document.");
    } finally {
      setBusy(false);
    }
  };

  const onOverlayMouseDown = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  useEffect(() => {
    const onKeyDown = (ev) => {
      if (ev.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => modalRef.current?.focus?.(), 0);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!canPick) return;
    normalizeAndSetFiles(e.dataTransfer.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!canPick) return;
    setDragOver(true);
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const getDocName = (d) => d?.filename || "PDF";
  const getDocSize = (d) => d?.size_bytes || d?.size || null;

  const getDocUrl = (d) => {
    const p = d?.storage_path || d?.path || d?.ruta || d?.file || d?.archivo;
    const normalized = normalizePath(p) || p;
    return normalized ? fileURL(normalized) : null;
  };

  return (
    <div className="omx-docs-admin-scope omx-docs-modal__overlay" role="dialog" aria-modal="true" aria-labelledby="omxDocsTitle" onMouseDown={onOverlayMouseDown}>
      <div className="omx-docs-modal" ref={modalRef} tabIndex={-1}>
        <div className="omx-docs-modal__header">
          <div>
            <div className="omx-docs-modal__title" id="omxDocsTitle">Order Documents</div>
            <div className="omx-docs-modal__sub">
              Order <b>#{pedidoId}</b> · Max <b>{MAX_DOCS}</b> PDFs
            </div>
          </div>

          <button type="button" className="omx-docs-modal__close" onClick={onClose} disabled={isBusy} aria-label="Close" title="Close">
            ✕
          </button>
        </div>

        <div className="omx-docs-modal__body">
          {error ? <div className="omx-docs-modal__alert">{error}</div> : null}

          <div className="omx-docs-grid">
            <div className="omx-docs-uploader">
              <div className={`omx-docs-dropzone ${dragOver ? "is-dragOver" : ""} ${!canPick ? "is-disabled" : ""}`} onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}>
                <input ref={inputRef} id="omxDocsInput" className="omx-docs-fileInput" type="file" accept="application/pdf,.pdf" multiple onChange={onPickFiles} disabled={!canPick} />

                <label htmlFor="omxDocsInput" className="omx-docs-pickBtn" title={remaining === 0 ? "Max PDFs reached" : "Choose PDF files"}>
                  <span className="omx-docs-pickIcon">📄</span>
                  <span className="omx-docs-pickText">{remaining === 0 ? "Limit reached" : "Choose or drop PDFs"}</span>
                  <span className="omx-docs-pickSub">
                    {selected.length ? `${selected.length} selected` : remaining === 0 ? "0 remaining" : `Up to ${remaining}`}
                  </span>
                </label>

                <div className="omx-docs-dropzone__hint">Drag & drop your PDF files here, or click to browse.</div>
              </div>

              <div className="omx-docs-uploader__meta">
                <div className="omx-docs-uploader__stats">
                  Existing: <b>{existingCount}</b> / {MAX_DOCS} · Remaining: <b>{remaining}</b>
                </div>

                {selected.length ? (
                  <div className="omx-docs-uploader__chips" aria-label="Selected files">
                    {selected.map((f) => (
                      <span key={f.name} className="omx-docs-chip">
                        <span className="omx-docs-chip__name" title={f.name}>{f.name}</span>
                        <span className="omx-docs-chip__meta">{formatMB(f.size)}</span>
                        <button type="button" className="omx-docs-chip__x" onClick={() => removeSelected(f.name)} disabled={isBusy} title="Remove" aria-label={`Remove ${f.name}`}>
                          ✕
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="omx-docs-uploader__hint">Select up to {remaining} PDF(s) to upload.</div>
                )}
              </div>

              <div className="omx-docs-uploader__actions">
                <button type="button" className="omx-docs-btn" onClick={clearPicker} disabled={isBusy || !selected.length}>
                  Clear
                </button>
                <button type="button" className="omx-docs-btn omx-docs-btn--primary" onClick={upload} disabled={!canUpload}>
                  {busy ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>

            <div className="omx-docs-list">
              <div className="omx-docs-list__head">
                <div className="omx-docs-list__title">Uploaded PDFs</div>
                {loading ? <div className="omx-docs-list__loading">Loading...</div> : null}
              </div>

              {!loading && (!docs || docs.length === 0) ? (
                <div className="omx-docs-empty">
                  <div className="omx-docs-empty__icon">📎</div>
                  <div className="omx-docs-empty__title">No documents yet</div>
                  <div className="omx-docs-empty__sub">Upload PDFs to keep order-related paperwork in one place.</div>
                </div>
              ) : (
                <div className="omx-docs-items">
                  {docs.map((d, idx) => {
                    const url = getDocUrl(d);
                    return (
                      <div key={d?.id ?? idx} className="omx-docs-item">
                        <div className="omx-docs-item__info">
                          <div className="omx-docs-item__name" title={getDocName(d)}>{getDocName(d)}</div>
                          <div className="omx-docs-item__meta">
                            {d?.created_at ? String(d.created_at).split("T")[0] : ""}
                            {getDocSize(d) ? ` · ${formatMB(getDocSize(d))}` : ""}
                          </div>
                        </div>

                        <div className="omx-docs-item__actions">
                          {url ? (
                            <a className="omx-docs-btn omx-docs-btn--ghost" href={url} target="_blank" rel="noopener noreferrer">
                              View
                            </a>
                          ) : null}

                          <button type="button" className="omx-docs-btn omx-docs-btn--danger" onClick={() => deleteDoc(d)} disabled={isBusy}>
                            Delete
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="omx-docs-modal__footer">
          <button className="omx-docs-btn" onClick={onClose} disabled={isBusy}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default OrderDocsModal;
